<template>
  <TagSelector
    class="tags-search-filter"
    :tags="tagStore.tags"
    :selected-tags="selectedTags"
    :show-add-button="selectedTags.length > 0"
    show-inclusion-options
    @tag-click="toggleTag"
    @selected-tag-click="toggleTag"
  >
    <template #placeholder>
      <div
        v-if="selectedTags.length === 0"
        class="tags__input-placeholder w-100"
      >
        Search
      </div>
    </template>
  </TagSelector>
</template>

<script lang="ts">
import Vue from "vue"
import { maybeCastArray, maybeCastNumber } from "@evercam/shared/utils"
import { mapStores } from "pinia"
import { useTagStore } from "~/stores/tag"
import TagSelector from "@/components/tags/TagSelector.vue"
import { useReportStore } from "@/stores/report"
import { TagSelectorItem } from "@evercam/shared/types"

export default Vue.extend({
  name: "TagSearchFilter",
  components: {
    TagSelector,
  },
  props: {
    fieldName: {
      type: String,
      required: true,
    },
    fieldNamesMap: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedTags: [],
    }
  },
  computed: {
    ...mapStores(useTagStore, useReportStore),
    exclusionFieldName(): string {
      return `no_${this.fieldName}`
    },
    searchFilters(): Record<string, number[]> {
      const defaultFilters = {
        [this.fieldName]: [],
        [this.exclusionFieldName]: [],
      }

      return this.selectedTags.reduce((acc, tag) => {
        let fieldName = this.fieldName
        if (tag.isExcluded) {
          fieldName = `no_${this.fieldName}`
        }

        return {
          ...acc,
          [fieldName]: [...acc[fieldName], tag.id],
        }
      }, defaultFilters)
    },
  },
  watch: {
    "$route.query": {
      handler() {
        this.initSelectedTags()
      },
      immediate: true,
      deep: true,
    },
    "tagStore.tags": {
      handler() {
        this.initSelectedTags()
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    this.initSelectedTags()
    this.$root.$on("toggle-tag", this.toggleTag)
  },
  methods: {
    initSelectedTags() {
      let querySelectedTagIds = maybeCastArray(
        this.$route.query[this.fieldName] || [],
        true
      ).map(maybeCastNumber)
      let queryExcludedTagIds = maybeCastArray(
        this.$route.query[this.exclusionFieldName] || [],
        true
      ).map(maybeCastNumber)

      this.selectedTags = this.tagStore.tags.reduce((acc, t) => {
        if (queryExcludedTagIds.includes(t.id)) {
          return [...acc, { ...t, isExcluded: true }]
        } else if (querySelectedTagIds.includes(t.id)) {
          return [...acc, t]
        }

        return acc
      }, [])
    },
    isSelected(tag: TagSelectorItem): boolean {
      return this.selectedTags.some((t) => t.id === tag.id)
    },
    toggleTag(tag: TagSelectorItem) {
      const isSelected = this.isSelected(tag)
      this.selectedTags = isSelected
        ? this.selectedTags.filter((t) => t.id !== tag.id)
        : [...this.selectedTags, tag]

      this.$emit("search", this.searchFilters)
    },
  },
})
</script>

<style lang="scss">
.tags-search-filter {
  &.tags {
    .tags__selected-items {
      border-bottom: 1px solid rgba(0, 0, 0, 0.42);
      min-height: 30px;
    }
    .tags__input-placeholder {
      color: #9d9d9d;
      transform: translateY(3px);
      cursor: text;
    }
  }
}
</style>
